import SixDigitCodeInput from "@via/components/code-input/six-digit-code-input.tsx";
import { mfaErrorMessage } from "@via/pages/authentication/mfa/mfa.api.tsx";
import { LockedNotice } from "@via/pages/authentication/mfa/mfa-notices.tsx";
import { type JSX, useState } from "react";

const PANEL = "space-y-3 rounded-md p-4 ring-1 ring-gray-200";

/** One TOTP code to authorize one action. Recovery codes are deliberately not offered here. */
export default function CodeConfirm<T>({
  title,
  action,
  onDone,
  onCancel,
}: {
  title: string;
  action: (code: string) => Promise<T>;
  onDone: (result: T) => void;
  onCancel: () => void;
}): JSX.Element {
  const [error, setError] = useState<ReturnType<typeof mfaErrorMessage> | null>(
    null
  );
  const [pending, setPending] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const lockedUntil = error?.code === "locked" ? error.lockedUntil : undefined;

  const run = async (code: string) => {
    setPending(true);
    setError(null);
    let result: T;
    try {
      result = await action(code);
    } catch (e) {
      setError(mfaErrorMessage(e));
      setAttempt(a => a + 1);
      setPending(false);
      return;
    }
    setPending(false);
    onDone(result);
  };

  return (
    <div className={PANEL}>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600">
        Enter a code from your authenticator app.
      </p>
      <SixDigitCodeInput
        resetKey={attempt}
        disabled={pending || lockedUntil !== undefined}
        onComplete={code => void run(code)}
      />
      {lockedUntil ? (
        <LockedNotice until={lockedUntil} />
      ) : (
        error && (
          <p role="alert" className="text-sm font-semibold text-rose-600">
            {error.message}
          </p>
        )
      )}
      <button
        type="button"
        className="text-sm font-semibold text-gray-700 hover:text-gray-900"
        onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
